import type { Logger } from '@happyvertical/logger';

/**
 * Job context for logging
 */
export interface JobContext {
  jobId: string;
  attempt: number;
  queue: string;
  objectType: string;
  method: string;
}

export interface JobEventInput {
  type?: string;
  level?: 'debug' | 'info' | 'warn' | 'error';
  stage?: string | null;
  progress?: number | null;
  message?: string;
  data?: Record<string, unknown>;
}

export interface JobProgressInput {
  stage: string;
  progress: number;
  message?: string;
  detail?: string;
  source?: string;
  data?: Record<string, unknown>;
}

export interface JobExecutionContext {
  job: JobContext & { tenantId?: string | null };
  logger: Logger;
  event(input: JobEventInput): Promise<void>;
  progress(input: JobProgressInput): Promise<void>;
  log(
    level: 'debug' | 'info' | 'warn' | 'error',
    message: string,
    data?: Record<string, unknown>,
  ): Promise<void>;
  /**
   * Present only for a job created by the MCP Tasks extension. Calling this
   * persists an input request and resolves after `tasks/update` supplies every
   * requested key (or rejects cooperatively if the task is cancelled).
   */
  task?: {
    requestInput(
      inputRequests: Record<string, unknown>,
    ): Promise<Record<string, unknown>>;
  };
}

/**
 * Logger extension that auto-injects job context into all log entries
 *
 * During job execution, all logs automatically include:
 * - jobId: The job's unique identifier
 * - attempt: Which attempt this is (1, 2, 3...)
 * - queue: Which queue the job is in
 * - objectType: The type of object being operated on
 * - method: The method being invoked
 */
export class JobContextLogger implements Logger {
  constructor(
    private readonly baseLogger: Logger,
    private readonly jobContext: JobContext,
  ) {}

  private addContext(data?: Record<string, unknown>): Record<string, unknown> {
    return {
      ...data,
      _job: {
        id: this.jobContext.jobId,
        attempt: this.jobContext.attempt,
        queue: this.jobContext.queue,
        objectType: this.jobContext.objectType,
        method: this.jobContext.method,
      },
    };
  }

  debug(message: string, data?: Record<string, unknown>): void {
    this.baseLogger.debug(message, this.addContext(data));
  }

  info(message: string, data?: Record<string, unknown>): void {
    this.baseLogger.info(message, this.addContext(data));
  }

  warn(message: string, data?: Record<string, unknown>): void {
    this.baseLogger.warn(message, this.addContext(data));
  }

  error(message: string, data?: Record<string, unknown>): void {
    this.baseLogger.error(message, this.addContext(data));
  }
}

export default JobContextLogger;
